import {
  computed,
  Directive,
  effect,
  type EffectRef,
  inject,
  Injector,
  type OnInit,
  runInInjectionContext,
  type Signal,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { type PctFormGroup, type WithForm, disableAbstractControlWhileLoading } from '@pct-ng/form';
import type { AbstractDtoService } from '@pct-ng/core/dto';
import type { AbstractControl, ɵValue } from '@angular/forms';
import { openWarningToast } from '@pct-ng/feature-message-material';
import { BackendErrorWithMessageError, FieldError, handleHttpError } from '@pct-ng/core/error-http';
import { navigateToParentRoute } from '@pct-ng/core/router';
import { assertIsNotNil } from '@pct-ts/assert';
import type { Observable } from 'rxjs';
import { i18nPrefixConcat, type TranslateAbleText } from '@pct-ng/i18n';
import { TranslocoService } from '@jsverse/transloco';
import { ABSTRACT_DETAILS_FORM_REGISTER_FIELD_ERRORS_RESOLVER } from './abstract-details-form-register-field-errors-resolver';
import { openFormErrorToast } from '../functions/open-form-error-toast';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ɵIsAny<T, Y, N> = 0 extends 1 & T ? Y : N;

@Directive({})
export abstract class AbstractDetailsComponent<
  GET_DTO,
  GET_DTO_ID_KEY extends keyof GET_DTO,
  LIST_DTO,
  PUT_DTO,
  // Korábban az alábbi típus megszorítás volt érvényben, de az egyik funkciónál ez problémát
  // okozott mivel a formba autómatikusan a getDTO kerül betöltésre és az egyik esetben nem
  // egyezett meg a getDTO a PUT_DTO-val
  //     FORM extends { [K in keyof PUT_DTO]: AbstractControl },
  FORM extends { [key: string]: AbstractControl },
  GET_DTO_ID_PARAMETER_TYPE = GET_DTO[GET_DTO_ID_KEY],
>
  implements OnInit, WithForm<FORM>
{
  readonly #abstractDetailsFormRegisterFieldErrorsResolver = inject(
    ABSTRACT_DETAILS_FORM_REGISTER_FIELD_ERRORS_RESOLVER,
  );

  protected abstract readonly i18nPrefixBase: string;
  protected abstract readonly i18nPrefix: string;
  protected readonly injector = inject(Injector);
  protected readonly translocoService = inject(TranslocoService);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected disabledControlPaths: string[] = [];
  protected abstract readonly service: AbstractDtoService<
    any,
    PUT_DTO,
    GET_DTO,
    LIST_DTO,
    GET_DTO_ID_KEY,
    any,
    any,
    GET_DTO_ID_PARAMETER_TYPE
  >;

  protected readonly loading = signal(true);
  protected readonly idParameter: Signal<GET_DTO_ID_PARAMETER_TYPE | null> = toSignal(
    this.route.paramMap.pipe(
      map(paramMap => paramMap.get('id') as GET_DTO_ID_PARAMETER_TYPE | null),
    ),
    {
      requireSync: true,
    },
  );
  protected readonly isEditMode = computed(() => this.idParameter() !== null);
  protected editModel: GET_DTO | null = null;

  form!: PctFormGroup<FORM>;

  protected disableAbstractControlsWhileLoadingRef!: EffectRef;

  constructor() {
    effect(() => {
      // Reinit form
      this.idParameter();
      this.initializeEditMode();
    });
  }

  ngOnInit(): void {
    this.form = this.createForm();
    this.#abstractDetailsFormRegisterFieldErrorsResolver(
      this.form,
      this.translocoService,
      this.openHasErrorOnFromErrorToaster.bind(this),
    );
    this.form.formState = 'NOT_MODIFIED';

    this.disableAbstractControlsWhileLoadingRef = runInInjectionContext(this.injector, () =>
      disableAbstractControlWhileLoading(this.form, this.loading, this.disabledControlPaths),
    );
  }

  protected initializeEditMode(): void {
    if (this.isEditMode()) {
      this.loading.set(true);

      this.service.getById(this.idParameter()!).subscribe({
        next: dto => {
          if (this.dtoIsReadOnly(dto)) {
            this.editModePatchValue(dto);
            this.disableAbstractControlsWhileLoadingRef.destroy();
            this.loading.set(false);
            openWarningToast({
              title: this.getToasterTitle(),
              message: 'PAGES.COMMON.DETAILS.TOASTER_MESSAGES.NOT_MODIFIABLE',
            });
          } else {
            this.editModel = dto;
            this.editModePatchValue(dto);
            this.loading.set(false);
            this.form.formState = 'NOT_MODIFIED';
          }
        },
        error: handleHttpError(this.goBackToList.bind(this)),
      });
    } else {
      this.loading.set(false);
    }
  }

  /**
   * When we bind the editModel, we call this to patch the form
   */
  protected editModePatchValue(dto: GET_DTO): void {
    this.form.reset(this.getDtoToFormValue(dto), { emitEvent: false });
  }

  protected goBackToList(): void {
    navigateToParentRoute(this.router, this.route);
  }

  protected getDtoToFormValue(getDto: GET_DTO): ɵIsAny<
    FORM,
    {
      [key: string]: any;
    },
    Partial<{ [K in keyof FORM]: ɵValue<FORM[K]> }>
  > {
    return { ...getDto } as ɵIsAny<
      FORM,
      {
        [key: string]: any;
      },
      Partial<{ [K in keyof FORM]: ɵValue<FORM[K]> }>
    >;
  }

  protected create(putDto: PUT_DTO): Observable<GET_DTO> {
    return this.service.create(putDto);
  }

  protected update(putDto: PUT_DTO): Observable<GET_DTO | void> {
    assertIsNotNil(this.editModel, 'Edit model must be available for update operation');
    const dtoId = this.service.getDtoId(this.editModel);
    assertIsNotNil(dtoId, 'DTO ID must be available for update operation');
    return this.service.update(dtoId, putDto);
  }

  protected getHttpErrorHandler(): (err: unknown) => void {
    return this.form.handleHttpErrorsWithFieldErrors(err => {
      this.loading.set(false);

      if (err instanceof FieldError) {
        return;
      }

      if (!(err instanceof BackendErrorWithMessageError)) {
        this.openHasErrorOnFromErrorToaster();
      } else {
        this.handleBackendErrorMessage(err);
      }
    });
  }

  protected openHasErrorOnFromErrorToaster(): void {
    openFormErrorToast(this.getToasterTitle());
  }

  protected handleBackendErrorMessage(
    // TODO any-t megprobalni feloldani, tipus delegacioval
    // eslint-disable-next-line unused-imports/no-unused-vars
    error: BackendErrorWithMessageError<any>,
  ): void {}

  protected onSubmit(): void {
    this.form.setSubmitted();
    if (this.form.invalid) {
      openFormErrorToast(this.getToasterTitle());
    } else {
      this.loading.set(true);
      const request = this.formValueToPutDto(this.form.getRawValue());
      const isEditMode = this.isEditMode();

      this[isEditMode ? 'update' : 'create'](request).subscribe({
        next: () => {
          this.form.formState = isEditMode ? 'MODIFIED' : 'CREATED';
          this.goBackToList();
        },
        // TODO field error
        error: this.getHttpErrorHandler(),
      });
    }
  }

  protected getToasterTitle(): TranslateAbleText {
    return {
      translate: true,
      text: `COMMON.FORM.TOASTER.${this.isEditMode() ? 'UPDATE' : 'NEW'}`,
      translateParams: {
        name: this.translocoService.translate(i18nPrefixConcat('SINGULAR', this.i18nPrefixBase)),
      },
    };
  }

  protected abstract createForm(): PctFormGroup<FORM>;
  protected abstract formValueToPutDto(
    value: ReturnType<PctFormGroup<FORM>['getRawValue']>,
  ): PUT_DTO;

  protected dtoIsReadOnly(_dto: GET_DTO): boolean {
    return false;
  }
}
